import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

import { fetchOutlookSessionForUser, OutlookPageQuery, OutlookSessionForUserResponse } from "./outlookCreateOrderApi"
import { CreateOrderDialogMode } from "../../../shared/components/createOrder/createOrderTypes"
import { useCreateOrderUser } from "../../../shared/components/createOrder/hooks/useCreateOrderUser"
import { ensureOfficeInitializeWithLog, loadOfficeJs } from "./components/loadOfficeJs"
import { OutlookCreateOrderContent } from "./components/OutlookCreateOrderContent"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"
import { useLocaleSelect } from "../../localization/localizationHooks"
import { useApiQuery } from "../../../core/api/useApiQuery"

const transformData = (data: OutlookSessionForUserResponse) => data?.session?.values

export function OutlookCreateOrder() {
    const [searchParams] = useSearchParams()
    const { supportedLocales, selectedLocale, selectLocale } = useLocaleSelect()

    const flowParam = searchParams.get("flow") ?? null
    const modeParam = searchParams.get("mode") ?? null
    const orderId = searchParams.get("orderId")
    const parentOrigin = searchParams.get("parentOrigin") || "*"
    const sessionUid = searchParams.get("sessionUid") ?? null
    const localeParam = searchParams.get("locale") ?? null
    // Per-order opt-in recorded by the add-in on the previous save; honoured only in edit mode.
    const initialShareOutlookAttendees = searchParams.get("shareOutlookAttendees") === "1"
    // Only the add-in can tell a shared calendar from the user's own. Absent and "0" must stay
    // distinct: an add-in too old to send the flag reports nothing, and treating that as "own
    // calendar" would strip a shared calendar of its own organizer entry.
    const sharedCalendarParam = searchParams.get("sharedCalendar")
    const isSharedCalendar = sharedCalendarParam === null ? undefined : sharedCalendarParam === "1"
    // Absent must stay `undefined`, never 0 — 0 means "nobody invited", and `Number(null)` is 0.
    const inviteeCountParam = searchParams.get("inviteeCount")
    const parsedInviteeCount = inviteeCountParam ? Number(inviteeCountParam) : NaN
    const outlookInviteeCount =
        Number.isInteger(parsedInviteeCount) && parsedInviteeCount >= 0 ? parsedInviteeCount : undefined
    const isValidLocaleParam = !!localeParam && supportedLocales.includes(localeParam)
    const isLocaleSyncing = isValidLocaleParam && localeParam !== selectedLocale

    const mode = orderId ? CreateOrderDialogMode.EDIT : CreateOrderDialogMode.CREATE

    const { onBehalf, setOnBehalf } = useCreateOrderUser({
        mode,
    })

    const { data: session, isFetching } = useApiQuery(fetchOutlookSessionForUser, {
        queryName: OutlookPageQuery.OUTLOOK_SESSION_FOR_USER,
        dependencies: {
            userId: onBehalf.userId,
            sessionUid: sessionUid!,
        },
        enabled: !!sessionUid,
        select: transformData,
    })

    useEffect(() => {
        if (isLocaleSyncing) {
            selectLocale(localeParam)
        }
    }, [isLocaleSyncing, selectLocale])

    useEffect(() => {
        let disposed = false

        ensureOfficeInitializeWithLog(() => {
            try {
                const parentOrigin = new URLSearchParams(location.search).get("parentOrigin") || "*"
                const payload = { type: "ping:init", ts: new Date().toISOString() }
                Office.context.ui.messageParent(JSON.stringify(payload), { targetOrigin: parentOrigin })
            } catch (e) {
                console.error("messageParent error", e)
            }
        })
        ;(async () => {
            try {
                await loadOfficeJs()

                const readyOrTimeout = await Promise.race([
                    Office.onReady(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error("onReady timeout (10s)")), 10000)),
                ])

                if (disposed) return

                console.log("Office.onReady resolved", readyOrTimeout)
            } catch (e) {
                console.error("office.js init error", e)
                console.log("testOutlookInit check:", {
                    href: location.href,
                    referrer: document.referrer,
                    inIFrame: window.self !== window.top,
                    hasOfficeObj: !!(window as any).Office,
                    hasOnReady: !!(window as any).Office?.onReady,
                })
            }
        })()

        return () => {
            disposed = true
        }
    }, [])

    // Values seeded from the session are localized once, so the `locale` param has to be applied
    // before the flow mounts — otherwise they are built in the default locale.
    if (isFetching || isLocaleSyncing) {
        return <LoadingSpinner />
    }

    return (
        <OutlookCreateOrderContent
            orderId={orderId ? +orderId : undefined}
            mode={mode}
            flowParam={flowParam}
            modeParam={modeParam}
            onBehalf={onBehalf}
            setOnBehalf={setOnBehalf}
            parentOrigin={parentOrigin}
            session={session}
            initialShareOutlookAttendees={initialShareOutlookAttendees}
            isSharedCalendar={isSharedCalendar}
            outlookInviteeCount={outlookInviteeCount}
        />
    )
}
